import * as React from "react";
import {
  AudioWaveform,
  Gauge,
  UserPen,
  Monitor,
  DatabaseZap,
  Building2,
  Plus,
  Users,
  Server
} from "lucide-react";

import { NavMain } from "@/components/layout/nav-main";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useSidebar } from "@/components/ui/sidebar";
import { useUser } from "@/context/user-provider";
import { useNavigationMenu } from "@/hooks/useNavigationMenu";

// Organization switcher component for multi-tenancy support
function OrganizationSwitcher() {
  const { 
    organizations, 
    activeOrganization, 
    switchOrganization, 
    isOrgLoading, 
    isLoading: isAuthLoading 
  } = useUser();
  const { open } = useSidebar();

  // If loading or no organizations, show appropriate message
  if (isOrgLoading || isAuthLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (organizations.length === 0) {
    if (!open) {
      // Collapsed sidebar - show just the icon
      return (
        <div className="flex items-center justify-center p-2">
          <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
            <Building2 className="size-4" />
          </div>
        </div>
      );
    } else {
      // Expanded sidebar - show full content
      return (
        <div className="p-2">
          <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg mb-2">
            <Building2 className="size-4" />
          </div>
          <div className="text-xs text-muted-foreground text-center">
            No organizations
          </div>
          <Button 
            size="sm" 
            className="w-full mt-2"
            variant="outline"
            onClick={() => window.location.href = '/console/organizations'}
          >
            <Plus className="size-3.5 mr-1" />
            Create Organization
          </Button>
        </div>
      );
    }
  }

  // When sidebar is collapsed, show only the icon
  if (!open) {
    return (
      <div className="p-2 flex justify-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Building2 className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-48 rounded-lg max-h-64"
            align="start"
            side="right"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Organizations
            </DropdownMenuLabel>
            <div className="max-h-48 overflow-y-auto">
              {organizations.map((org) => {
                const isActive = activeOrganization?.organizationId === org.id;
                return (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={async () => {
                      if (!isActive) {
                        await switchOrganization(org.id);
                      }
                    }}
                    className={`gap-2 p-2 ${isActive ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border">
                      <Building2 className="size-3.5 shrink-0" />
                    </div>
                    <div className="flex-1 truncate">
                      {org.name}
                      {isActive && (
                        <span className="text-xs text-muted-foreground ml-1">(active)</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="gap-2 p-2"
              onClick={() => window.location.href = '/console/organizations'}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <Plus className="size-4" />
              </div>
              <div className="text-muted-foreground font-medium">Add organization</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // When sidebar is expanded, show full content
  return (
    <div className="p-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between p-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div className="flex items-center gap-2 flex-1 text-left">
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-6 items-center justify-center rounded-lg">
                <Building2 className="size-3.5" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {activeOrganization?.organization?.name || organizations[0]?.name || 'Select Organization'}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {activeOrganization?.role || 'member'}
                </span>
              </div>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg max-h-64"
          align="start"
          side="right"
          sideOffset={4}
        >
          <DropdownMenuLabel className="text-muted-foreground text-xs">
            Organizations
          </DropdownMenuLabel>
          <div className="max-h-48 overflow-y-auto">
            {organizations.map((org) => {
              const isActive = activeOrganization?.organizationId === org.id;
              return (
                <DropdownMenuItem
                  key={org.id}
                  onClick={async () => {
                    if (!isActive) {
                      await switchOrganization(org.id);
                    }
                  }}
                  className={`gap-2 p-2 ${isActive ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <div className="flex size-6 items-center justify-center rounded-md border">
                    <Building2 className="size-3.5 shrink-0" />
                  </div>
                  <div className="flex-1 truncate">
                    {org.name}
                    {isActive && (
                      <span className="text-xs text-muted-foreground ml-1">(active)</span>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="gap-2 p-2"
            onClick={() => window.location.href = '/console/organizations'}
          >
            <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
              <Plus className="size-4" />
            </div>
            <div className="text-muted-foreground font-medium">Add organization</div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  // Get navigation menu data - pure consumer pattern!
  // The hook handles all complexity of fetching, filtering, and state management
  const { navItems, isLoading } = useNavigationMenu();

  // Add icons to navigation items based on item ID (i18n-safe)
  // This mapping will eventually come from the backend
  const navItemsWithIcons = React.useMemo(() => {
    return navItems.map(item => {
      // Map section IDs to their corresponding icons
      // Using ID instead of title ensures icons persist with i18n
      const iconMap: Record<string, any> = {
        'dashboard': Gauge,
        'ai-personas': UserPen,
        'kiosks': Monitor,
        'my-assets': DatabaseZap,
        'services': Server,
        'admin': Users,
        'settings': AudioWaveform,
        'organizations': Building2,
      };

      return {
        ...item,
        icon: iconMap[item.id] || null,
      };
    });
  }, [navItems]);

  // Show loading state while navigation is being fetched
  if (isLoading) {
    return (
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <OrganizationSwitcher />
        </SidebarHeader>
        <SidebarContent>
          <div className="p-4 text-center">Loading...</div>
        </SidebarContent>
        <SidebarRail />
      </Sidebar>
    );
  }

  // Pure rendering - component just displays the data it receives
  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <OrganizationSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navItemsWithIcons} />
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
